import PropTypes from 'prop-types'
import styled from 'styled-components/macro'

import { Layout, Tag, Text } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { formatDate, formats } from 'core/utils/date'

import { Cargo } from 'legacy/components/LoadOffer/Cargo'
import { Equipment } from 'legacy/components/LoadOffer/Equipment'
import { RequirementLabel } from 'legacy/components/LoadOffer/RequirementLabel'
import {
  Description,
  Label,
  LeftMarginBox,
  Section,
  UppercaseHeading,
} from 'legacy/components/LoadOffer/Section'
import { ALL_MODES } from 'legacy/constants/equipmentTypes'
import { screen } from 'legacy/styles/screen'
import { OFFER_TYPES, getLoadOfferType } from 'legacy/utils/getLoadOfferType'

import { useConfigContext } from 'config/ui/ConfigContext'

import { getRequirementsFromLoad } from '../../utils/getRequirementsFromLoad'
import { LoadStop } from './LoadStop'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${screen.md} {
    min-height: 400px;
  }
`

const Main = styled.main`
  display: flex;
  flex-direction: column;
  padding: 16px;
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const Title = styled.h2`
  font-weight: bold;
  font-size: 18px;
  white-space: nowrap;
`

const Route = styled.section`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin: 8px 0 16px;
`

const CustomerRequirementsList = styled.ul`
  padding-left: 1px;

  & > li {
    position: relative;

    padding: 2px 0 3px 14px;

    font-size: 13px;

    &::before {
      position: absolute;
      top: 8px;
      left: 0;

      width: 6px;
      height: 6px;

      background-color: ${getToken('color-neutral-dark')};
      border-radius: 50%;

      content: '';
    }
  }
`

const HorizontalDivider = styled.hr`
  width: 100%;
  height: 0;
  margin-top: 3px;
  margin-bottom: 12px;

  border-color: ${getToken('color-neutral-light')};
  border-top: none;
`

const RequirementItem = styled.div`
  display: block;
  margin-top: ${getToken('space-s')};
`

function LoadInformation({ load }) {
  const {
    commodity,
    customer_requirements,
    dimensions,
    equipment_type,
    equipment_subtypes,
    stops,
    temperature,
    weight,
    mode,
    must_arrive_by_date,
  } = load

  const showPTL = useConfigContext().showPTL.toBoolean()

  const offerType = getLoadOfferType(load)
  const isLoadCandidate = offerType === OFFER_TYPES.LOAD_CANDIDATE

  const requirements = getRequirementsFromLoad(load)
  const customerRequirements = customer_requirements || []

  const isPTL = mode === ALL_MODES.PTL.value
  const mustArriveByDate = isPTL ? new Date(must_arrive_by_date) : null

  return (
    <Wrapper>
      <Main>
        <Header>
          <Title>Load Information</Title>
        </Header>

        <Route>
          {stops &&
            stops.map(stop => (
              <LoadStop key={stop.stop_index} stop={stop} isLoadCandidate={isLoadCandidate} />
            ))}
        </Route>
        <HorizontalDivider />
        {showPTL && isPTL && (
          <>
            <Section data-testid="must-arrive-by">
              <Layout.Group justify="flex-start" space="s">
                <UppercaseHeading variant="heading-sm-bold" $mr="0.2rem">
                  Must arrive by:
                </UppercaseHeading>
                <Text>{formatDate(mustArriveByDate, formats.shortdate)}</Text>
                <Text>{formatDate(mustArriveByDate, formats.twentyfour)}</Text>
              </Layout.Group>
            </Section>
            <HorizontalDivider />
          </>
        )}
        <Section>
          <Layout.Group>
            <div>
              <Label>Equipment Type</Label>
              <Description>
                <Equipment
                  equipmentType={equipment_type}
                  equipmentSubtypes={equipment_subtypes}
                  temperature={temperature}
                  mode={showPTL ? mode : undefined}
                  showLabel
                />
              </Description>
            </div>
            {showPTL ? (
              <LeftMarginBox>
                <Label>Mode</Label>
                <Description bold>{mode}</Description>
              </LeftMarginBox>
            ) : null}
          </Layout.Group>
        </Section>
        {requirements.length > 0 && (
          <Section>
            <Label>Requirements</Label>
            <div>
              {requirements.map(requirement => (
                <RequirementItem key={requirement.type}>
                  <Tag size="default" variant="warning">
                    <RequirementLabel requirement={requirement} />
                  </Tag>
                </RequirementItem>
              ))}
            </div>
          </Section>
        )}
        <Section>
          <Label>Commodity</Label>
          <Description>
            <Cargo boldWeight weight={weight} commodity={commodity} dimensions={dimensions} />
          </Description>
        </Section>

        {customerRequirements.length > 0 && (
          <Section>
            <Label>Instructions</Label>
            <Description>
              <CustomerRequirementsList>
                {customerRequirements.map(someRequirement => (
                  <li key={someRequirement}>{someRequirement}</li>
                ))}
              </CustomerRequirementsList>
            </Description>
          </Section>
        )}
      </Main>
    </Wrapper>
  )
}

LoadInformation.propTypes = {
  load: PropTypes.shape({}),
}

LoadInformation.defaultProps = {
  load: undefined,
}

export { LoadInformation }
